// Picture-in-picture call card: where each corner puts it and which corner a
// drag lands in. Shared by web and native.

export type PipCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
export type PipPoint = { x: number; y: number }
export type PipSize = { width: number; height: number }
export type PipInsets = { top: number; right: number; bottom: number; left: number }

export const PIP_CORNERS: readonly PipCorner[] = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
]
export const DEFAULT_PIP_CORNER: PipCorner = 'bottom-right'
// A fling carries the card this many seconds further before picking a corner.
const FLING_S = 0.18

export function isPipCorner(v: unknown): v is PipCorner {
  return typeof v === 'string' && (PIP_CORNERS as readonly string[]).includes(v)
}

/** Top-left of the card sitting in `corner`, kept on screen when the space is too small. */
export function pipCornerPosition(
  corner: PipCorner,
  viewport: PipSize,
  card: PipSize,
  insets: PipInsets,
): PipPoint {
  const maxX = Math.max(insets.left, viewport.width - insets.right - card.width)
  const maxY = Math.max(insets.top, viewport.height - insets.bottom - card.height)
  return {
    x: corner.endsWith('left') ? insets.left : maxX,
    y: corner.startsWith('top') ? insets.top : maxY,
  }
}

/** The corner nearest the card's centre, projected along the release velocity (px/s). */
export function nearestPipCorner(
  position: PipPoint,
  viewport: PipSize,
  card: PipSize,
  velocity: PipPoint = { x: 0, y: 0 },
): PipCorner {
  const cx = position.x + card.width / 2 + velocity.x * FLING_S
  const cy = position.y + card.height / 2 + velocity.y * FLING_S
  const vertical = cy < viewport.height / 2 ? 'top' : 'bottom'
  const horizontal = cx < viewport.width / 2 ? 'left' : 'right'
  return `${vertical}-${horizontal}`
}

/** Keeps a dragged card inside the viewport. */
export function clampPipPosition(position: PipPoint, viewport: PipSize, card: PipSize): PipPoint {
  return {
    x: Math.min(Math.max(0, position.x), Math.max(0, viewport.width - card.width)),
    y: Math.min(Math.max(0, position.y), Math.max(0, viewport.height - card.height)),
  }
}
